import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useAuth0 } from '@auth0/auth0-react';
import { bookingAPI, giftCardAPI, userAPI, packageAPI, paymentAPI, couponCodeAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useUser } from '../contexts/UserContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { getErrorMessage } from '../utils/apiErrorHandler';
import { BookingCart } from '../components/BookingCart';
import { SubscriptionContract, PackageContract } from '@shared/types';
import LocalizedLink from '../components/LocalizedLink';
import { useServiceMetaStore } from '../stores/serviceMetaStore';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import PhoneInput from '../components/PhoneInput';
import { formatPrice } from '@shared/utils';
import { formatInTimeZone as formatInTimeZoneHelper } from 'date-fns-tz';

const TALLINN_TIMEZONE = 'Europe/Tallinn';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface LocationState {
  service: {
    id: string;
    name: string;
    name_et?: string;
    duration: number;
    price?: number;
    currency?: string;
    maxParticipants?: number;
  };
  date: string;
  time: string;
  slotId: string;
  resourceId?: string;
}

interface BooklaPackage {
  id: string;
  name: string;
  name_en?: string;
  name_et?: string;
  name_ru?: string;
  name_fi?: string;
  type: 'bulk' | 'subscription';
}

const PaymentForm: React.FC<{
  servicePrice: number;
  onPaymentMethodCreated: (paymentMethodId: string) => void;
  onPaymentError: (error: string) => void;
  triggerPayment: boolean;
  setTriggerPayment: (val: boolean) => void;
  name: string;
  email: string;
  onPaymentFocus?: () => void;
}> = ({ servicePrice, onPaymentMethodCreated, onPaymentError, triggerPayment, setTriggerPayment, name, email, onPaymentFocus }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();

  useEffect(() => {
    if (triggerPayment && stripe && elements) {
      const createPaymentMethod = async () => {
        try {
          const card = elements.getElement(CardNumberElement);
          if (!card) {
            throw new Error('Card element not found');
          }

          const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: card,
            billing_details: {
              name: name,
              email: email,
            },
          });

          if (pmError) {
            throw new Error(pmError.message || 'Payment method creation failed');
          }

          if (!paymentMethod) {
            throw new Error('Payment method not created');
          }

          onPaymentMethodCreated(paymentMethod.id);
        } catch (err: any) {
          onPaymentError(err.message || 'Payment failed');
        } finally {
          setTriggerPayment(false);
        }
      };

      createPaymentMethod();
    }
  }, [triggerPayment, stripe, elements, onPaymentMethodCreated, onPaymentError, setTriggerPayment, name, email]);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          {t.checkout?.cardNumber || 'Card Number'}
        </label>
        <div className="border border-gray-300 rounded-md p-4">
          <CardNumberElement
            onFocus={onPaymentFocus}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#32325d',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {t.checkout?.expiryDate || 'Expiry Date'}
          </label>
          <div className="border border-gray-300 rounded-md p-4">
            <CardExpiryElement
              onFocus={onPaymentFocus}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#32325d',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            {t.checkout?.cvc || 'CVC'}
          </label>
          <div className="border border-gray-300 rounded-md p-4">
            <CardCvcElement
              onFocus={onPaymentFocus}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#32325d',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingConfirmPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const { user: auth0User, isAuthenticated, loginWithRedirect } = useAuth0();
  const { t, language } = useI18n();
  useDocumentTitle(t.booking.confirmYourBooking);
  useNoIndex();
  const { user, isLoadingUser } = useUser();
  const { items: cartItems, clearCart } = useBookingCartStore();
  const { credits: serviceCredits } = useServiceMetaStore();
  
  const state = location.state as LocationState | undefined;
  
  const [participants, setParticipants] = useState(1);

  const totalRequiredCredits = useMemo(() => {
    if (cartItems.length > 0) {
      return cartItems.reduce((sum, item) => {
        const credits = serviceCredits[item.serviceId] || 1;
        return sum + credits;
      }, 0);
    } else if (state) {
      const credits = serviceCredits[state.service.id] || 1;
      return credits;
    }
    return 0;
  }, [cartItems, state, serviceCredits]);

  // Phone number state - using full international format (e.g., "+3725123456")
  const [phoneValue, setPhoneValue] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  
  // Guest user states
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [guestCheckout, setGuestCheckout] = useState(false);
  
  // Gift card states
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [applyingGiftCard, setApplyingGiftCard] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState(false);

  // Coupon code states
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(false);
  const [couponPricing, setCouponPricing] = useState<{
    originalPrice: number;
    couponPrice: number;
    discount: number;
    sessionsIncluded: number;
    sessionsToApplyDiscount?: number;
    sessionsAlreadyUsed?: number;
    sessionsRemainingOnCoupon?: number;
  } | null>(null);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  
  // Subscription and package contract states
  const [subscriptionContracts, setSubscriptionContracts] = useState<SubscriptionContract[]>([]);
  const [packageContracts, setPackageContracts] = useState<PackageContract[]>([]);
  const [unavailableSubscriptions, setUnavailableSubscriptions] = useState<SubscriptionContract[]>([]);
  const [unavailablePackages, setUnavailablePackages] = useState<PackageContract[]>([]);
  const [selectedContract, setSelectedContract] = useState<string>('new_payment');
  const [allPackages, setAllPackages] = useState<BooklaPackage[]>([]);
  
  // Payment states
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [triggerPayment, setTriggerPayment] = useState(false);
  const [isNavigatingToSuccess, setIsNavigatingToSuccess] = useState(false);

  const totalAvailableCredits = useMemo(() => {
    const subCredits = subscriptionContracts.reduce((sum, c) => {
      const remaining = c.sessionsRemaining ?? ((c.limitations?.bookingsCount ?? 0) - (c.usages?.length || 0));
      return sum + remaining;
    }, 0);
    
    const pkgCredits = packageContracts.reduce((sum, c) => {
      const remaining = c.sessionsRemaining ?? ((c.limitations?.bookingsCount ?? 0) - (c.usages?.length || 0));
      return sum + remaining;
    }, 0);
    
    return subCredits + pkgCredits;
  }, [subscriptionContracts, packageContracts]);

  const isGlobalInsufficient = totalAvailableCredits < totalRequiredCredits;
  const hasTrackedBeginCheckout = useRef(false);
  const hasFiredPaymentInfo = useRef(false);

  // Redirect if no state and no cart items (but not when navigating to success)
  useEffect(() => {
    if (!state && cartItems.length === 0 && !isSubmitting && !isNavigatingToSuccess) {
      navigate('/book');
    }
  }, [state, cartItems, navigate, isSubmitting, isNavigatingToSuccess]);

  useEffect(() => {
    if ((cartItems.length > 0 || state) && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true;
      clearEcommerce();
      
      const items = cartItems.length > 0
        ? cartItems.map(item => {
            const variant = formatInTimeZoneHelper(new Date(item.startTime), TALLINN_TIMEZONE, 'dd_MM_yyyy_HH:mm');
            return {
              item_id: item.serviceId,
              item_name: item.serviceName,
              item_category: 'Service',
              item_variant: variant,
              location_id: 'Tallinn'
            };
          })
        : state
          ? [{
              item_id: state.service.id,
              item_name: state.service.name,
              item_category: 'Service',
              item_variant: `${state.date.replace(/-/g, '_')}_${state.time}`,
              location_id: 'Tallinn'
            }]
          : [];

      pushToDataLayer('begin_checkout', {
        content: { category: 'booking_service' },
        ecommerce: { items }
      });
    }
  }, [cartItems, state]);

  const trackPaymentInfo = (paymentType: string = 'pay_with_card') => {
    if (!hasFiredPaymentInfo.current && (cartItems.length > 0 || state)) {
      hasFiredPaymentInfo.current = true;
      clearEcommerce();
      
      const items = cartItems.length > 0
        ? cartItems.map(item => {
            const variant = formatInTimeZoneHelper(new Date(item.startTime), TALLINN_TIMEZONE, 'dd_MM_yyyy_HH:mm');
            return {
              item_id: item.serviceId,
              item_name: item.serviceName,
              item_category: 'Service',
              item_variant: variant,
              location_id: 'Tallinn'
            };
          })
        : state
          ? [{
              item_id: state.service.id,
              item_name: state.service.name,
              item_category: 'Service',
              item_variant: `${state.date.replace(/-/g, '_')}_${state.time}`,
              location_id: 'Tallinn'
            }]
          : [];

      pushToDataLayer('add_payment_info', {
        content: { category: 'booking_service' },
        ecommerce: {
          payment_type: paymentType,
          items
        }
      });
    }
  };

  // Fetch all available packages and subscriptions for name lookup
  useEffect(() => {
    const fetchAllPackages = async () => {
      try {
        const response = await packageAPI.getAll();
        const packagesData = (response.data as any)?.data?.packages || response.data;
        const packagesArray = Array.isArray(packagesData) ? packagesData : [];
        setAllPackages(packagesArray);
      } catch (err) {
        console.error('Error fetching all packages:', err);
      }
    };
    fetchAllPackages();
  }, []);

  // Fetch user profile for phone number, name, and email
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isAuthenticated && !isLoadingUser) {
        try {
          const response = await userAPI.getProfile();

          // Initialize name and email from profile (preferred over Auth0)
          setName(response.data.name || '');
          setEmail(response.data.email || '');

          // Initialize phone from profile
          if (response.data.phoneNumber) {
            setPhoneValue(response.data.phoneNumber);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          // Fallback to Auth0 user if profile fetch fails
          if (auth0User) {
            setName(auth0User.name || '');
            setEmail(auth0User.email || '');
          }
        }
      }
    };

    fetchUserProfile();
  }, [isAuthenticated, isLoadingUser, auth0User]);

  // Load guest information from localStorage when component mounts
  useEffect(() => {
    if (!isAuthenticated) {
      const savedGuestName = localStorage.getItem('guest_name');
      const savedGuestEmail = localStorage.getItem('guest_email');
      const savedGuestPhone = localStorage.getItem('guest_phone');

      if (savedGuestName) {
        setGuestName(savedGuestName);
      }
      if (savedGuestEmail) {
        setGuestEmail(savedGuestEmail);
      }
      if (savedGuestPhone) {
        setPhoneValue(savedGuestPhone);
      }
    }
  }, [isAuthenticated]);
  
  // Fetch user's subscription and package contracts
  useEffect(() => {
    const fetchContracts = async () => {
      if (isAuthenticated && !isLoadingUser && user) {
        try {
          // Fetch both subscriptions and packages in parallel
          const [subscriptionsRes, packagesRes] = await Promise.all([
            userAPI.getSubscriptions(),
            userAPI.getPackages()
          ]);
          
          const userSubscriptions = subscriptionsRes.data || [];
          const userPackages = packagesRes.data || [];
          
          // Filter available and unavailable contracts using the isAvailable flag from backend
          const activeSubscriptions = userSubscriptions.filter((contract) => {
            return (contract as any).isAvailable !== false;
          });

          const activePackages = userPackages.filter((contract) => {
            return (contract as any).isAvailable !== false;
          });
          
          // Filter unavailable contracts (expired or no sessions remaining)
          const unavailableSubscriptionsList = userSubscriptions.filter((contract) => {
            return (contract as any).isAvailable === false;
          });

          const unavailablePackagesList = userPackages.filter((contract) => {
            return (contract as any).isAvailable === false;
          });
          
          setSubscriptionContracts(activeSubscriptions as any);
          setPackageContracts(activePackages as any);
          setUnavailableSubscriptions(unavailableSubscriptionsList as any);
          setUnavailablePackages(unavailablePackagesList as any);
          
          // Set default payment option to first available subscription/package
          if (activeSubscriptions.length > 0) {
            setSelectedContract(activeSubscriptions[0].id);
          } else if (activePackages.length > 0) {
            setSelectedContract(activePackages[0].id);
          }
        } catch (err) {
          console.error('Error fetching user contracts:', err);
        }
      }
    };

    fetchContracts();
  }, [isAuthenticated, isLoadingUser, user]);

  useEffect(() => {
    if (selectedContract === 'new_payment') return;

    if (isGlobalInsufficient) {
      setSelectedContract('new_payment');
    }
  }, [selectedContract, isGlobalInsufficient]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const dateLocale = language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined;
    return formatInTimeZone(date, TALLINN_TIMEZONE, 'EEE, MMM d, yyyy', { locale: dateLocale });
  };

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError(t.booking.enterGiftCardCode);
      return;
    }

    setApplyingGiftCard(true);
    setGiftCardError(null);

    try {
      const response = await giftCardAPI.getBalance(giftCardCode.trim());
      setGiftCardBalance(response.data.balance);
      setAppliedGiftCard(true);
      setGiftCardError(null);
    } catch (err: any) {
      setGiftCardError(
        err.response?.status === 404 
          ? t.booking.invalidGiftCardCode
          : t.booking.giftCardError
      );
      setGiftCardBalance(null);
      setAppliedGiftCard(false);
    } finally {
      setApplyingGiftCard(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setGiftCardCode('');
    setGiftCardBalance(null);
    setAppliedGiftCard(false);
    setGiftCardError(null);
  };

  // Coupon code handlers
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError(t.coupon?.errors?.missingCode || 'Please enter a coupon code.');
      return;
    }

    // Determine email for validation
    const validationEmail = isAuthenticated ? email : guestEmail;
    if (!validationEmail) {
      setCouponError(t.coupon?.errors?.missingEmail || 'Email address is required to validate the coupon.');
      return;
    }

    setApplyingCoupon(true);
    setCouponError(null);

    try {
      // Get total credits required and prices from cart
      // Combined services like Contrast Therapy require multiple credits per booking
      let sessionCount = 0;
      const cartPrices: number[] = [];

      if (cartItems.length > 0) {
        for (const item of cartItems) {
          const credits = serviceCredits[item.serviceId] || 1;
          const priceInCents = Math.round((item.price || 0) * 100);
          for (let i = 0; i < credits; i++) {
            cartPrices.push(Math.round(priceInCents / credits));
          }
          sessionCount += credits;
        }
      } else if (state) {
        const credits = serviceCredits[state.service.id] || 1;
        const priceInCents = state.service.price ? Math.round(state.service.price * 100) : 0;
        for (let i = 0; i < credits; i++) {
          cartPrices.push(Math.round(priceInCents / credits));
        }
        sessionCount = credits;
      }

      const response = await couponCodeAPI.validate(
        couponCode.trim().toUpperCase(),
        validationEmail,
        sessionCount,
        language,
        cartPrices
      );

      const result = response.data;

      if (result.valid) {
        setAppliedCoupon(true);
        setCouponPricing({
          originalPrice: result.pricing.originalPrice,
          couponPrice: result.pricing.couponPrice,
          discount: result.pricing.discount,
          sessionsIncluded: result.pricing.sessionsIncluded,
          sessionsToApplyDiscount: result.pricing.sessionsToApplyDiscount,
          sessionsAlreadyUsed: result.pricing.sessionsAlreadyUsed,
          sessionsRemainingOnCoupon: result.pricing.sessionsRemainingOnCoupon
        });
        setCouponMessage(result.message);
        setCouponError(null);
        // When coupon is applied, automatically select new_payment since it handles the special pricing
        setSelectedContract('new_payment');
        // Clear any applied gift card since coupons and gift cards cannot be combined
        setGiftCardCode('');
        setGiftCardBalance(null);
        setAppliedGiftCard(false);
        setGiftCardError(null);
      } else {
        setCouponError(result.message || t.coupon?.errors?.validationError || 'Invalid coupon code.');
        setAppliedCoupon(false);
        setCouponPricing(null);
        setCouponMessage(null);
      }
    } catch (err: any) {
      setCouponError(t.coupon?.errors?.validationError || 'An error occurred while validating the coupon code.');
      setAppliedCoupon(false);
      setCouponPricing(null);
      setCouponMessage(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(false);
    setCouponPricing(null);
    setCouponMessage(null);
    setCouponError(null);
  };

  // Handle phone change for PhoneInput component
  const handlePhoneChange = (value: string) => {
    setPhoneValue(value);
    // Save full phone number to localStorage for guest users
    if (!isAuthenticated && value) {
      setTimeout(() => localStorage.setItem('guest_phone', value), 500);
    }
  };


  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Save guest information to localStorage
  const saveGuestInfoToStorage = (name: string, email: string) => {
    if (name.trim()) {
      localStorage.setItem('guest_name', name.trim());
    }
    if (email.trim()) {
      localStorage.setItem('guest_email', email.trim());
    }
  };

  // Clear guest information from localStorage
  const clearGuestInfoFromStorage = () => {
    localStorage.removeItem('guest_name');
    localStorage.removeItem('guest_email');
    localStorage.removeItem('guest_phone');
  };

  const validateGuestFields = () => {
    if (!isAuthenticated && guestCheckout) {
      // For guest users who chose to checkout as guest, validate name and email
      if (!guestName.trim()) {
        setError(t.booking.nameRequired);
        return false;
      }
      if (!guestEmail.trim()) {
        setEmailError(t.booking.emailRequired);
        return false;
      }
      if (!validateEmail(guestEmail)) {
        setEmailError(t.booking.emailInvalid);
        return false;
      }

      // Save guest information to localStorage for future use
      saveGuestInfoToStorage(guestName, guestEmail);
    }
    return true;
  };

  const handlePaymentMethodCreated = async (pmId: string) => {
    setPaymentError(null);

    if (!state && cartItems.length === 0) return;

    try {
      // Process payment immediately after payment method is created
      const totalPrice = cartItems.length > 0
        ? cartItems.reduce((sum, item) => sum + (item.price || 0), 0)
        : (state?.service.price || 0);

      // Use coupon price if coupon is applied, otherwise use total price
      const priceToCharge = appliedCoupon && couponPricing ? couponPricing.couponPrice : totalPrice;

      // Subtract gift card balance from the price to charge
      const gcDeduction = appliedGiftCard && giftCardBalance ? Math.min(giftCardBalance, priceToCharge) : 0;
      const adjustedPrice = priceToCharge - gcDeduction;

      // If the adjusted price is 0 (e.g., fully covered by gift card or coupon), skip payment and proceed directly
      if (adjustedPrice <= 0) {
        await proceedWithBooking();
        return;
      }

      const paymentIntentResponse = await paymentAPI.createBookingPaymentIntent({
        serviceId: cartItems.length > 0 ? cartItems[0].serviceId : state!.service.id,
        price: adjustedPrice,
      });

      const { clientSecret } = paymentIntentResponse.data as { clientSecret: string; paymentIntentId?: string };

      // Confirm the payment using Stripe
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: pmId,
      });

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed');
      }

      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment was not successful');
      }
      
      // Payment succeeded, now proceed with booking
      await proceedWithBooking(paymentIntent.id);
    } catch (paymentErr: any) {
      console.error('Payment error:', paymentErr);
      setPaymentError(paymentErr.message || 'Payment failed');
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    setIsSubmitting(false);
  };

  const proceedWithBooking = async (piId?: string) => {
    try {
      const fullPhoneNumber = phoneValue;

      // Gift card redemption is now handled by the backend in the bulk booking endpoint

      // Always use bulk booking flow
      const bulkBookingData = {
        items: cartItems.length > 0
          ? cartItems.map(item => ({
              cartItemId: item.cartItemId,
              serviceId: item.serviceId,
              startTime: item.startTime,
              duration: item.duration,
              // Include invitees if they exist for multi-participant bookings
              ...(item.invitees && item.invitees.length > 0 ? { invitees: item.invitees } : {})
            }))
          : state
          ? [{
              cartItemId: 'single-booking',
              serviceId: state.service.id,
              startTime: (() => {
                const [year, month, day] = state.date.split('-').map(Number);
                const [hour, minute] = state.time.split(':').map(Number);
                const tallinnDateTime = new Date(year, month - 1, day, hour, minute);
                return fromZonedTime(tallinnDateTime, 'Europe/Tallinn').toISOString();
              })(),
              duration: state.service.duration
            }]
          : [],
        paymentDetails: piId ? { paymentIntentId: piId } : undefined,
        selectedPackageId: selectedContract === 'new_payment' ? undefined : selectedContract,
        giftCardCode: appliedGiftCard ? giftCardCode : undefined,
        couponCode: appliedCoupon ? couponCode : undefined,
        language,
        // Include guest information for guest checkout
        ...(guestCheckout ? {
          guestName: guestName,
          guestEmail: guestEmail,
          phoneNumber: fullPhoneNumber
        } : isAuthenticated ? {
          phoneNumber: fullPhoneNumber
        } : {})
      };

      const response = await bookingAPI.createBulk(bulkBookingData);
      
      if (response.data) {
        clearCart();

        // Save phone number for future bookings
        if (isAuthenticated) {
          try {
            await userAPI.updateProfile({ phoneNumber: fullPhoneNumber });
          } catch (err) {
            console.error('Error saving phone number to profile:', err);
          }
          // Clear guest info from localStorage since user is now authenticated
          clearGuestInfoFromStorage();
        } else {
          localStorage.setItem('user_phone', fullPhoneNumber);
          // For successful guest bookings, keep the guest info for next time
          // (it was already saved in validateGuestFields)
        }
        
        // Navigate to success page
        const responseData = response.data as any;
        const bookingCount = Array.isArray(responseData) ? responseData.length : (responseData.bookings?.length || 0);
        const orderId = responseData.orderId;

        // Set flag to prevent competing navigation
        setIsNavigatingToSuccess(true);

        if (orderId) {
          // Both authenticated and guest users with orderId can view booking details
          navigate(`/booking-success/${orderId}`);
        } else if (isAuthenticated) {
          navigate('/account', {
            state: {
              message: bookingCount > 1
                ? (t.booking.bulkBookingSuccess?.replace('{{count}}', bookingCount.toString()) || `Successfully created ${bookingCount} bookings`)
                : t.booking.success,
              type: 'success'
            }
          });
        } else {
          // Fallback for guest checkout without orderId (shouldn't happen with bulk booking)
          navigate('/booking-success', {
            state: {
              guestBooking: true,
              bookingCount,
              ...(state ? {
                service: state.service,
                date: state.date,
                time: state.time,
              } : {})
            },
          });
        }
      }
    } catch (err: any) {
      console.error('Error creating booking:', err);
      
      if (err.response?.status === 409) {
        setError(getErrorMessage(err, t, 'Some time slots are no longer available. Please go back and update your cart.'));
      } else {
        setError(getErrorMessage(err, t, t.booking.bookingFailed));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setAttemptedSubmit(true);

    if (!isPhoneValid || !phoneValue) {
      return;
    }

    if (!validateGuestFields()) {
      return;
    }

    if (!state && cartItems.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    setPaymentError(null);

    const totalPrice = cartItems.length > 0
      ? cartItems.reduce((sum, item) => sum + (item.price || 0), 0)
      : (state?.service.price || 0);

    // Use coupon price if coupon is applied, otherwise use total price
    const effectivePrice = appliedCoupon && couponPricing ? couponPricing.couponPrice : totalPrice;

    // Subtract gift card balance from the effective price
    const gcDeduction = appliedGiftCard && giftCardBalance ? Math.min(giftCardBalance, effectivePrice) : 0;
    const finalPriceToCharge = effectivePrice - gcDeduction;

    // If payment is required (new_payment selected and final price > 0), trigger payment form
    if (selectedContract === 'new_payment' && (isAuthenticated || guestCheckout) && finalPriceToCharge > 0) {
      setTriggerPayment(true);
      return;
    }
    
    // Track payment info for subscription/package credits if not already tracked
    if (selectedContract !== 'new_payment') {
      const isSubscription = subscriptionContracts.some(c => c.id === selectedContract);
      trackPaymentInfo(isSubscription ? 'subscription_credits' : 'package_credits');
    }
    
    // Otherwise proceed directly with booking
    await proceedWithBooking();
  };

  if (!state && cartItems.length === 0) {
    return null;
  }

  const totalPrice = cartItems.length > 0
    ? cartItems.reduce((sum, item) => sum + (item.price || 0), 0)
    : (state?.service.price || 0);

  const giftCardDeduction = appliedGiftCard && giftCardBalance ? Math.min(giftCardBalance, totalPrice) : 0;
  const priceAfterGiftCard = totalPrice - giftCardDeduction;
  const effectiveFinalPrice = appliedCoupon && couponPricing
    ? couponPricing.couponPrice
    : priceAfterGiftCard;

  return (
    <div className="py-8 pb-20 md:pb-8 max-w-2xl mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {cartItems.length > 0 ? t.checkout?.title || 'Checkout' : t.booking.confirmYourBooking}
        </h1>
        <p className="text-lg text-gray-600">
          {cartItems.length > 0
            ? t.checkout?.bookingInfo || 'Review and confirm your bookings'
            : t.booking.confirmYourDetails}
        </p>
      </div>

      {cartItems.length > 0 ? (
        <BookingCart hideCheckoutButton={true} />
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {t.booking.bookingDetails}
          </h3>
          <div className="space-y-3 text-gray-700">
            {state ? (
              <>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.serviceLabel}</span>
                  <span className="font-medium">{getTranslatedField(state.service, 'name', language)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.dateLabel}</span>
                  <span className="font-medium">{formatDate(state.date)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.timeLabel}</span>
                  <span className="font-medium">{state.time}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.durationLabel}</span>
                  <span className="font-medium">{state.service.duration} {t.booking.minutes}</span>
                </div>
                {state.service.maxParticipants && state.service.maxParticipants > 1 && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>{t.booking.participantsLabel}</span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setParticipants(Math.max(1, participants - 1))}
                        disabled={participants <= 1}
                        className="px-2 py-0.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        -
                      </button>
                      <span className="font-medium">{participants} / {state.service.maxParticipants}</span>
                      <button
                        onClick={() => setParticipants(Math.min(state.service.maxParticipants || 1, participants + 1))}
                        disabled={participants >= (state.service.maxParticipants || 1)}
                        className="px-2 py-0.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
            
            <div className="border-t pt-4">
              <div className="flex justify-between font-bold text-lg">
                <span>{t.checkout?.total || 'Total'}</span>
                <span>{formatPrice(totalPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">{t.booking.contactInformation}</h3>
        <div className="space-y-4">
          {isAuthenticated ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.account.name} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.account.phoneNumber} <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  onValidation={setIsPhoneValid}
                  placeholder={t.account.phoneNumber}
                  required
                  error={attemptedSubmit && !phoneValue ? t.booking.phoneNumberRequired : undefined}
                />
                {!attemptedSubmit && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t.booking.phoneNumberRequired}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              {!guestCheckout ? (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    {t.booking.chooseHowToProceed}
                  </p>
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => setGuestCheckout(true)}
                      className="px-6 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 font-medium"
                    >
                      {t.booking.continueAsGuest}
                    </button>
                    <button
                      onClick={() => loginWithRedirect({ 
                        appState: { 
                          returnTo: window.location.pathname + window.location.search,
                          state: location.state 
                        }
                      })}
                      className="px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 font-medium"
                    >
                      {t.booking.loginToBook}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.account.name} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setGuestName(newName);
                        // Save to localStorage as user types (with minimal debouncing)
                        if (newName.trim()) {
                          setTimeout(() => localStorage.setItem('guest_name', newName.trim()), 500);
                        }
                      }}
                      placeholder={t.booking.enterYourName}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        attemptedSubmit && !guestName.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {attemptedSubmit && !guestName.trim() && (
                      <p className="mt-1 text-sm text-red-600">{t.booking.nameRequired}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.account.email} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => {
                        const newEmail = e.target.value;
                        setGuestEmail(newEmail);
                        setEmailError(null);
                        // Save to localStorage as user types (with minimal debouncing)
                        if (newEmail.trim() && validateEmail(newEmail)) {
                          setTimeout(() => localStorage.setItem('guest_email', newEmail.trim()), 500);
                        }
                      }}
                      placeholder={t.booking.enterYourEmail}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        emailError || (attemptedSubmit && !guestEmail.trim()) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {(emailError || (attemptedSubmit && !guestEmail.trim())) && (
                      <p className="mt-1 text-sm text-red-600">{emailError || t.booking.emailRequired}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.account.phoneNumber} <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      onValidation={setIsPhoneValid}
                      placeholder={t.account.phoneNumber}
                      required
                      error={attemptedSubmit && !phoneValue ? t.booking.phoneNumberRequired : undefined}
                    />
                    {!attemptedSubmit && (
                      <p className="mt-1 text-sm text-gray-500">
                        {t.booking.phoneNumberRequired}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Subscription Contract Section - show for all authenticated users and guest checkout */}
      {(isAuthenticated || guestCheckout) && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t.booking.paymentOption || 'Payment Option'}</h3>
          <div className="space-y-3">
            {/* Global Insufficient Credits Warning */}
            {isGlobalInsufficient && (subscriptionContracts.length > 0 || packageContracts.length > 0) && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">
                  {(t.booking.insufficientCredits || "You have {{available}} sessions available, but you're trying to book {{required}} sessions.")
                    .replace('{{available}}', totalAvailableCredits.toString())
                    .replace('{{required}}', totalRequiredCredits.toString())}
                </p>
                <div className="mt-1 text-sm text-red-600">
                  {t.booking.insufficientCreditsActionStart || 'Please '}
                  <LocalizedLink 
                    to={cartItems.length > 0 ? '/book' : '/book/' + state?.service.id} 
                    className="underline hover:text-red-800"
                  >
                    {t.booking.insufficientCreditsActionModify || 'modify your booking'}
                  </LocalizedLink>
                  {t.booking.insufficientCreditsActionMiddle || ' or '}
                  <LocalizedLink to="/pricing" className="underline hover:text-red-800">
                    {t.booking.insufficientCreditsActionPurchase || 'purchase an additional package'}
                  </LocalizedLink>
                  {t.booking.insufficientCreditsActionEnd || '.'}
                </div>
              </div>
            )}
            {/* Display Subscriptions */}
            {subscriptionContracts.length > 0 && (
              <>
                <div className="font-semibold text-gray-700 mt-3 mb-1">{t.booking.subscriptions || 'Subscriptions'}</div>
                {subscriptionContracts.map(contract => {
                  const remainingSessions = contract.sessionsRemaining ??
                    ((contract.limitations?.bookingsCount ?? 0) - (contract.usages?.length || 0));
                  return (
                    <div key={contract.id} className={isGlobalInsufficient ? 'opacity-75' : ''}>
                      <label className={'flex items-center p-3 rounded-lg transition-colors ' + 
                        (isGlobalInsufficient ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50')
                      }>
                        <input
                          type="radio"
                          id={contract.id}
                          name="payment_option"
                          value={contract.id}
                          checked={selectedContract === contract.id}
                          onChange={(e) => {
                            setSelectedContract(e.target.value);
                            trackPaymentInfo('subscription_credits');
                            // Clear coupon code when using credits (coupon only works with card payment)
                            if (appliedCoupon || couponCode) {
                              handleRemoveCoupon();
                            }
                          }}
                          disabled={isGlobalInsufficient}
                          className="mr-3 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="font-medium">
                            {(() => {
                              const pkg = allPackages.find(p => p.id === contract.subscriptionID);
                              return pkg ? getTranslatedField(pkg, 'name', language) : contract.name || 'Subscription';
                            })()}
                          </span>
                          <div className="text-sm text-gray-600">
                            {t.booking.remainingSessions || 'Remaining sessions'}: {remainingSessions}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
            {/* Display Packages */}
            {packageContracts.length > 0 && (
              <>
                <div className="font-semibold text-gray-700 mt-3 mb-1">{t.booking.packages || 'Packages'}</div>
                {packageContracts.map(contract => {
                  const remainingSessions = contract.sessionsRemaining ??
                    ((contract.limitations?.bookingsCount ?? 0) - (contract.usages?.length || 0));
                  return (
                    <div key={contract.id} className={isGlobalInsufficient ? 'opacity-75' : ''}>
                      <label className={'flex items-center p-3 rounded-lg transition-colors ' + 
                        (isGlobalInsufficient ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50')
                      }>
                        <input
                          type="radio"
                          id={contract.id}
                          name="payment_option"
                          value={contract.id}
                          checked={selectedContract === contract.id}
                          onChange={(e) => {
                            setSelectedContract(e.target.value);
                            trackPaymentInfo('package_credits');
                            // Clear coupon code when using credits (coupon only works with card payment)
                            if (appliedCoupon || couponCode) {
                              handleRemoveCoupon();
                            }
                          }}
                          disabled={isGlobalInsufficient}
                          className="mr-3 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                           <span className="font-medium">
                            {(() => {
                              const pkg = allPackages.find(p => p.id === contract.subscriptionID);
                              return pkg ? getTranslatedField(pkg, 'name', language) : contract.name || 'Package';
                            })()}
                          </span>
                          <div className="text-sm text-gray-600">
                            {t.booking.remainingSessions || 'Remaining sessions'}: {remainingSessions}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
            {/* Unavailable Subscriptions and Packages Section */}
            {isAuthenticated && (unavailableSubscriptions.length > 0 || unavailablePackages.length > 0) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-2">
                  {t.booking?.unavailableTitle || 'Unavailable Subscriptions and Packages'}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t.booking?.unavailableMessage || 'These subscriptions and packages have expired or have been fully used.'}
                </p>
                <div className="space-y-3 opacity-60">
                  {unavailableSubscriptions.map(contract => {
                    const pkg = allPackages.find(p => p.id === contract.subscriptionID);
                    const translatedName = pkg ? getTranslatedField(pkg, 'name', language) : contract.name || 'Subscription';
                    const expirationDate = (contract as any).expirationDate || (contract as any).ends_at;
                    const unavailableReason = (contract as any).unavailableReason;
                    return (
                      <div key={contract.id} className="p-4 border rounded-lg bg-gray-50">
                        <h5 className="font-semibold text-gray-700">{translatedName}</h5>
                        <p className="text-sm text-gray-500">Subscription</p>
                        {unavailableReason === 'expired' && expirationDate && (
                          <p className="text-sm text-gray-500">
                            {t.booking?.expired || 'Expired'}: {new Date(expirationDate).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        )}
                        {unavailableReason === 'no_sessions' && (
                          <p className="text-sm text-gray-500">All sessions used</p>
                        )}
                      </div>
                    );
                  })}
                  {unavailablePackages.map(contract => {
                    const pkg = allPackages.find(p => p.id === contract.subscriptionID);
                    const translatedName = pkg ? getTranslatedField(pkg, 'name', language) : contract.name || 'Package';
                    const expirationDate = (contract as any).expirationDate || (contract as any).ends_at;
                    const unavailableReason = (contract as any).unavailableReason;
                    return (
                      <div key={contract.id} className="p-4 border rounded-lg bg-gray-50">
                        <h5 className="font-semibold text-gray-700">{translatedName}</h5>
                        <p className="text-sm text-gray-500">Package</p>
                        {unavailableReason === 'expired' && expirationDate && (
                          <p className="text-sm text-gray-500">
                            {t.booking?.expired || 'Expired'}: {new Date(expirationDate).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        )}
                        {unavailableReason === 'no_sessions' && (
                          <p className="text-sm text-gray-500">All sessions used</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Upsell message for users without packages or subscriptions */}
            {subscriptionContracts.length === 0 && packageContracts.length === 0 && (
              <div className="mt-4 mb-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-center">
                <p className="text-sm text-blue-800">
                  {t.upsell?.message || "Booking individual sessions? It's cheaper to use a subscription or package."}
                </p>
                <LocalizedLink
                  to="/pricing"
                  className="mt-2 inline-block text-sm font-semibold text-blue-600 hover:underline"
                >
                  {t.upsell?.linkText || 'View Packages & Memberships'}
                </LocalizedLink>
              </div>
            )}
            {appliedGiftCard && giftCardBalance !== null && giftCardBalance >= totalPrice ? (
              <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="font-medium text-green-800">
                  {t.booking.fullyCoveredByGiftCard || 'Your booking is fully covered by the gift card!'}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {t.booking.noPaymentRequired || 'No additional payment is required.'}
                </p>
              </div>
            ) : (
              <>
                <div className="font-semibold text-gray-700 mt-3 mb-1">{t.booking.payByCard || 'Pay by Card'}</div>
                <div key="new_payment">
                  <label className="flex items-center cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors">
                    <input
                      type="radio"
                      id="new_payment"
                      name="payment_option"
                      value="new_payment"
                      checked={selectedContract === 'new_payment'}
                      onChange={(e) => setSelectedContract(e.target.value)}
                      className="mr-3 text-teal-600 focus:ring-teal-500"
                    />
                    <div>
                      <span className="font-medium">{t.booking.payWithCard || 'Pay with Card'}</span>
                      {effectiveFinalPrice > 0 && (
                        <span className="text-gray-600 ml-2">({formatPrice(effectiveFinalPrice)})</span>
                      )}
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Coupon Code Section */}
      {(isAuthenticated || guestCheckout) && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t.coupon?.title || 'Coupon Code'}</h3>
          <div className="space-y-4">
            {!appliedCoupon ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.coupon?.haveCoupon || 'Have a coupon code?'}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onFocus={() => {
                      // Automatically select credit card payment when focusing on coupon code input
                      if (selectedContract !== 'new_payment') {
                        setSelectedContract('new_payment');
                      }
                    }}
                    placeholder={t.coupon?.placeholder || 'Enter coupon code'}
                    className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      couponError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={applyingCoupon || appliedGiftCard}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={applyingCoupon || !couponCode.trim() || appliedGiftCard}
                    className={`px-4 py-2 rounded-md font-medium ${
                      applyingCoupon || !couponCode.trim() || appliedGiftCard
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {applyingCoupon ? t.booking.applying : t.booking.apply}
                  </button>
                </div>
                {couponError && (
                  <p className="mt-1 text-sm text-red-600">{couponError}</p>
                )}
                {appliedGiftCard && (
                  <p className="mt-1 text-sm text-gray-500">{t.coupon?.cannotCombine || 'Coupon codes cannot be combined with gift cards.'}</p>
                )}
              </div>
            ) : (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium text-green-800">
                      {t.coupon?.applied || 'Coupon applied!'}
                    </p>
                    {couponPricing && (
                      <div className="mt-1 text-sm text-green-700">
                        {/* Show sessions already used if applicable */}
                        {couponPricing.sessionsAlreadyUsed !== undefined && couponPricing.sessionsAlreadyUsed > 0 && (
                          <p className="text-gray-600">
                            {t.coupon?.sessionsAlreadyUsed?.replace('{{count}}', String(couponPricing.sessionsAlreadyUsed)) ||
                              `${couponPricing.sessionsAlreadyUsed} session${couponPricing.sessionsAlreadyUsed > 1 ? 's' : ''} already used`}
                          </p>
                        )}
                        {/* Show remaining sessions on coupon */}
                        {couponPricing.sessionsRemainingOnCoupon !== undefined && couponPricing.sessionsAlreadyUsed === 0 && couponPricing.sessionsToApplyDiscount === 1 && (
                          <p className="text-blue-600">
                            {t.coupon?.bookAnotherLater || 'Book another session later to use your free session!'}
                          </p>
                        )}
                        <p className="font-bold text-lg mt-1">
                          {t.coupon?.totalPrice || 'Total'}: {formatPrice(couponPricing.couponPrice)}
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    {t.booking.remove || 'Remove'}
                  </button>
                </div>
                {couponMessage && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">{couponMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gift Card Section */}
      {selectedContract === 'new_payment' && !appliedCoupon && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t.booking.giftCard || 'Gift Card'}</h3>
          <div className="space-y-4">
            {!appliedGiftCard ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.booking.haveGiftCard || 'Have a gift card?'}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                    placeholder="GIFT-XXXX-XXXX-XXXX"
                    className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      giftCardError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={applyingGiftCard}
                  />
                  <button
                    onClick={handleApplyGiftCard}
                    disabled={applyingGiftCard || !giftCardCode.trim()}
                    className={`px-4 py-2 rounded-md font-medium ${
                      applyingGiftCard || !giftCardCode.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {applyingGiftCard ? t.booking.applying : t.booking.apply}
                  </button>
                </div>
                {giftCardError && (
                  <p className="mt-1 text-sm text-red-600">{giftCardError}</p>
                )}
              </div>
            ) : (
              <div className="flex justify-between items-center p-4 bg-green-50 border border-green-200 rounded-md">
                <div>
                  <p className="font-medium text-green-800">
                    {t.booking.giftCardApplied || 'Gift card applied'}
                  </p>
                  <p className="text-sm text-green-600">
                    {t.booking.balance || 'Balance'}: {formatPrice(parseFloat(String(giftCardBalance || 0)))}
                  </p>
                </div>
                <button
                  onClick={handleRemoveGiftCard}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  {t.booking.remove || 'Remove'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Details Section - Show when new_payment is selected and total price > 0 or coupon is applied */}
      {selectedContract === 'new_payment' && (isAuthenticated || guestCheckout) && effectiveFinalPrice > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-semibold">{t.checkout?.paymentDetails || 'Payment Details'}</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t.checkout?.securePaymentInfo || 'Your payment is securely processed by Stripe.'}</p>
          
          <div className="mb-4 space-y-2">
            <p className="text-sm text-gray-600 mb-2">{t.checkout?.customerInfo || 'Customer Information'}:</p>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t.checkout?.name || 'Name'}</label>
              <input
                type="text"
                id="name"
                value={guestCheckout ? guestName : name}
                onChange={(e) => {
                  const newName = e.target.value;
                  if (guestCheckout) {
                    setGuestName(newName);
                    // Save to localStorage as user types (with minimal debouncing)
                    if (newName.trim()) {
                      setTimeout(() => localStorage.setItem('guest_name', newName.trim()), 500);
                    }
                  } else {
                    setName(newName);
                  }
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t.checkout?.email || 'Email'}</label>
              <input
                type="email"
                id="email"
                value={guestCheckout ? guestEmail : email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  if (guestCheckout) {
                    setGuestEmail(newEmail);
                    // Save to localStorage as user types (with minimal debouncing)
                    if (newEmail.trim() && validateEmail(newEmail)) {
                      setTimeout(() => localStorage.setItem('guest_email', newEmail.trim()), 500);
                    }
                  } else {
                    setEmail(newEmail);
                  }
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
          </div>

          <Elements stripe={stripePromise}>
            <PaymentForm
              servicePrice={appliedCoupon && couponPricing ? couponPricing.couponPrice : totalPrice}
              onPaymentMethodCreated={handlePaymentMethodCreated}
              onPaymentError={handlePaymentError}
              triggerPayment={triggerPayment}
              setTriggerPayment={setTriggerPayment}
              name={guestCheckout ? guestName : name}
              email={guestCheckout ? guestEmail : email}
              onPaymentFocus={() => trackPaymentInfo('pay_with_card')}
            />
          </Elements>
          {paymentError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{paymentError}</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Cancellation Policy Notice */}
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
        <div className="flex">
          <svg className="h-5 w-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-amber-800">
            {t.account.cancellationPolicyNotice || 'Note: Bookings cannot be cancelled within 24 hours of the appointment time.'}
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => cartItems.length > 0 ? navigate('/book') : navigate(`/book/${serviceId}`)}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          {t.common.back}
        </button>
        <button
          onClick={(e) => {
            if (isSubmitting || !isPhoneValid || !phoneValue || (!isAuthenticated && !guestCheckout) || (!isAuthenticated && guestCheckout && (!guestName.trim() || !guestEmail.trim()))) {
              e.preventDefault();
              setAttemptedSubmit(true);
            } else {
              handleConfirm();
            }
          }}
          className={`px-6 py-2 rounded-md font-medium ${
            isSubmitting || !isPhoneValid || !phoneValue || (!isAuthenticated && !guestCheckout) || (!isAuthenticated && guestCheckout && (!guestName.trim() || !guestEmail.trim()))
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isSubmitting
            ? t.booking.confirming
            : selectedContract !== 'new_payment' || effectiveFinalPrice <= 0
            ? (cartItems.length > 1 ? t.checkout?.confirmBookings || 'Confirm Bookings' : t.booking.confirmBooking)
            : (cartItems.length > 1 ? t.checkout?.confirmBookings || 'Confirm Bookings' : t.booking.confirmAndPay)}
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmPage;